import React from "react";
import CardClass from "../../../CardClass";
import Setting from "../../../../Utility/Setting";
import EmptyState from "../../../EmptyState";
import Progress from "../../../Progress";
import { ResponsiveSankey } from "@nivo/sankey";
import MUIBox from "@mui/material/Box";
import MUICard from "@mui/material/Card";
import MUITypography from "@mui/material/Typography";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import MUIFileDownloadIcon from "@mui/icons-material/FileDownload";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIVisibilityIcon from "@mui/icons-material/Visibility";
import MUIVisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import MUIOpenInFullIcon from "@mui/icons-material/OpenInFull";
import MUICloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import Menu from "../../../Menu";

export default class CardReportOrderTimelineAuditChart extends CardClass {
  constructor(props) {
    super(props);
    const sankeyData = this.reduceSankeyData(props.sankeyRows);
    this.state = {
      sankeyData: sankeyData,
      sankeyChartCollapsed: props.sankeyChartCollapsed,
      otherCategoryCollapsed: props.otherCategoryCollapsed,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.sankeyRows !== this.props.sankeyRows) {
      const sankeyData = this.reduceSankeyData(this.props.sankeyRows);
      this.setState({ sankeyData: sankeyData });
    }
  }

  /**
   * Prevents cycles in one-way Sankey charts by deduplicating nodes and links.
   * Merges duplicate links and increases their weight accordingly.
   * Returns an object containing unique nodes and links.
   */
  reduceSankeyData(data) {
    if (!data || data.length === 0) return { nodes: [], links: [] };
    const nodesSet = new Set(["Other Reason"]);
    const linkMap = new Map();
    let valueTotal = 0;

    // Step 1: Build initial linkMap and nodeSet
    for (const { source, target } of data) {
      nodesSet.add(source.trim());
      nodesSet.add(target.trim());

      const key = `${source.trim()}->${target.trim()}`;
      const entry = linkMap.get(key);
      if (entry) {
        entry.value += 1;
      } else {
        linkMap.set(key, {
          source: source.trim(),
          target: target.trim(),
          value: 1,
        });
      }
      valueTotal += 1;
    }

    const unfilteredNodes = Array.from(nodesSet).map((id) => ({ id }));
    const rawLinks = Array.from(linkMap.values());

    // Step 2: Determine threshold dynamically
    let threshold =
      valueTotal > 5000
        ? Math.floor(valueTotal * 0.001)
        : Math.floor(valueTotal * 0.01);

    if (valueTotal < 1000) threshold = Math.floor(threshold * 0.1);
    if (threshold < 2) threshold = 1;

    // Step 3: Redirect weak links to "Other Reason"
    const otherCategoryCollapsed = this.state.otherCategoryCollapsed;

    if (otherCategoryCollapsed) {
      for (const link of rawLinks) {
        if (link.value <= threshold) {
          link.target = "Other Reason";
        }
      }
    }

    // Step 4: Track all node IDs that appear in links
    const linkedNodeIds = new Set();
    for (const link of rawLinks) {
      linkedNodeIds.add(link.source);
      linkedNodeIds.add(link.target);
    }

    // Step 5: Condense links with same source/target
    const condensedLinksMap = new Map();
    for (const { source, target, value } of rawLinks) {
      const key = `${source}|${target}`;
      condensedLinksMap.set(key, (condensedLinksMap.get(key) || 0) + value);
    }

    const links = Array.from(condensedLinksMap.entries()).map(
      ([key, value]) => {
        const [source, target] = key.split("|");
        return { source, target, value };
      },
    );

    // Step 6: Filter only nodes that appear in links
    const nodes = unfilteredNodes.filter((node) =>
      linkedNodeIds.has(node.id.trim()),
    );
    this.setState({
      sankeyData: { nodes, links, valueTotal },
    });
    return { nodes, links, valueTotal };
  }

  renderContent() {
    const isDarkMode = Setting.get("ui.theme") === "dark";
    const color = isDarkMode ? "#ffffff" : "#000000";
    const theme = isDarkMode ? "accent" : "category10";
    const userAgent = window.navigator.userAgent;
    const isFireFox = userAgent.includes("Firefox") ? false : true;
    const isCollapsed = this.state.sankeyChartCollapsed;
    if (!this.props.isLoading) {
      if (isCollapsed) {
        return (
          <EmptyState
            line1="The Chart is hidden"
            line2='Select "View Chart" from the dropdown to view'
          />
        );
      }
      if (this.props.sankeyRows && this.props.sankeyRows.length > 0) {
        const baseHeight = 200;
        const heightPerNode = 30;
        const nodeCount = this.state.sankeyData?.nodes?.length || 0;
        const calculatedHeight = baseHeight + nodeCount * heightPerNode;
        return (
          <MUIBox
            id="sankey-container"
            style={{ height: calculatedHeight, width: "100%" }}
          >
            {this.state.sankeyData.nodes?.length > 0 &&
              this.state.sankeyData?.links?.length > 0 && (
                <ResponsiveSankey
                  sort="descending"
                  data={this.state.sankeyData}
                  margin={{ top: 60, right: 50, bottom: 60, left: 50 }}
                  align="justify"
                  onClick={(node) => {
                    this.props.onSankeyClick(node);
                  }}
                  colors={{ scheme: theme }}
                  nodeOpacity={1}
                  nodeHoverOthersOpacity={0.35}
                  nodeThickness={18}
                  nodeBorderWidth={0}
                  nodeBorderColor={{
                    from: "color",
                    modifiers: [["darker", 0.8]],
                  }}
                  nodeBorderRadius={3}
                  linkOpacity={0.5}
                  linkColor="source"
                  linkHoverOthersOpacity={0.1}
                  linkContract={3}
                  enableLinkGradient={isFireFox}
                  labelPadding={16}
                  labelTextColor={color}
                  nodeTooltip={(node) => {
                    //TODO A tooltip for hovering over the node - not sure if necessary, as it defaults to just showing the OSR
                    // This is removing it entirely for now.
                    return null;
                  }}
                  linkTooltip={(link) => {
                    let linkPercentageOfTotal =
                      (link.link.value / this.state.sankeyData.valueTotal) *
                      100;
                    if (
                      linkPercentageOfTotal < 0.1 &&
                      linkPercentageOfTotal > 0
                    ) {
                      linkPercentageOfTotal = linkPercentageOfTotal.toFixed(2);
                    } else if (linkPercentageOfTotal < 1) {
                      linkPercentageOfTotal = linkPercentageOfTotal.toFixed(1);
                    } else {
                      linkPercentageOfTotal = linkPercentageOfTotal.toFixed(0);
                    }

                    if (!link?.link?.source || !link?.link?.target) {
                      return null;
                    }
                    return (
                      <div
                        ref={(el) => {
                          if (el) {
                            const width = el.getBoundingClientRect().width + 30;
                            const isRightSide =
                              event.clientX > window.innerWidth / 2;
                            const translateX = isRightSide ? 0 : width;
                            el.style.transform = `translateX(${translateX}px)`;
                          }
                        }}
                        style={{
                          transition:
                            "transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)",
                        }}
                      >
                        <MUICard
                          style={{
                            background: this.getBackground(),
                            padding: "4px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <MUITypography
                            variant="body2"
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <span
                              style={{
                                backgroundColor: link.link.source.color,
                                width: "12px",
                                height: "12px",
                                marginRight: "6px",
                                flexShrink: 0,
                              }}
                            ></span>
                            <span
                              style={{
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                              }}
                            >
                              {link.link.source.id}
                            </span>
                            <span style={{ margin: "0 4px", flexShrink: 0 }}>
                              →
                            </span>
                            <span
                              style={{
                                backgroundColor: link.link.target.color,
                                width: "12px",
                                height: "12px",
                                marginRight: "6px",
                                flexShrink: 0,
                              }}
                            ></span>
                            <span
                              style={{
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                              }}
                            >
                              {link.link.target.id}
                            </span>
                            <span style={{ marginLeft: "4px", flexShrink: 0 }}>
                              <strong>
                                (
                                {`${link.link.value} • ${linkPercentageOfTotal}%`}
                                )
                              </strong>
                            </span>
                          </MUITypography>
                        </MUICard>
                      </div>
                    );
                  }}
                  linkBlendMode="normal"
                ></ResponsiveSankey>
              )}
          </MUIBox>
        );
      } else {
        return (
          <EmptyState
            line1={
              this.props.loadedAt ? "No results found" : "Waiting for results"
            }
            line2={
              this.props.loadedAt
                ? "Try adjusting your settings"
                : "Please select your settings"
            }
          />
        );
      }
    }
    return <Progress message="Loading Data..." />;
  }

  /**
   * @returns {string} The title of the card.
   */
  getTitle() {
    return "Chart";
  }

  getActions() {
    const handleDownload = () => {
      const svgElement = document.querySelector("#sankey-container svg");
      const svgBlob = new Blob([svgElement.outerHTML], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(svgBlob);

      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = svgElement.clientWidth;
        canvas.height = svgElement.clientHeight;
        const context = canvas.getContext("2d");

        context.drawImage(image, 0, 0);
        URL.revokeObjectURL(url);

        canvas.toBlob((blob) => {
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = `Order Timeline Audit - ${new Date().toISOString().slice(0, 10)}.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }, "image/png");
      };

      image.src = url;
    };

    const handleCategoryCollasping = () => {
      const otherCategoryCollapsed = !this.state.otherCategoryCollapsed;
      Setting.set(
        `${this.props.getDetailKey}.otherCategoryCollapsed`,
        otherCategoryCollapsed,
      );
      this.setState(
        {
          otherCategoryCollapsed: otherCategoryCollapsed,
        },
        () => {
          this.reduceSankeyData(this.props.sankeyRows);
        },
      );
    };

    const handleChartCollapsing = () => {
      const sankeyChartCollapsed = !this.state.sankeyChartCollapsed;
      const sankeyData = this.reduceSankeyData(this.props.sankeyRows);
      Setting.set(
        `${this.props.getDetailKey}.sankeyChartCollapsed`,
        sankeyChartCollapsed,
      );
      this.setState({
        sankeyData: sankeyData,
        sankeyChartCollapsed: sankeyChartCollapsed,
      });
    };
    return [
      <Menu
        menuItems={[
          <div id="menu-items">
            <MUIMenuItem
              disabled={
                this.props.isLoading ||
                !this.props.sankeyRows ||
                (this.props.sankeyRows && this.props.sankeyRows.length === 0) ||
                this.state.sankeyChartCollapsed === true
              }
              onClick={handleDownload}
            >
              <MUIListItemIcon>
                <MUIFileDownloadIcon fontSize="small" />
              </MUIListItemIcon>
              Download
            </MUIMenuItem>
            <MUIMenuItem
              disabled={
                this.props.isLoading ||
                !this.props.sankeyRows ||
                (this.props.sankeyRows && this.props.sankeyRows.length === 0) ||
                this.state.sankeyChartCollapsed === true
              }
              onClick={handleCategoryCollasping}
            >
              <MUIListItemIcon>
                {this.state.otherCategoryCollapsed ? (
                  <MUIOpenInFullIcon fontSize="small" />
                ) : (
                  <MUICloseFullscreenIcon fontSize="small" />
                )}
              </MUIListItemIcon>
              {this.state.otherCategoryCollapsed
                ? "Expand Low Routes"
                : "Combine Low Routes"}
            </MUIMenuItem>
            <MUIMenuItem
              disabled={
                this.props.isLoading ||
                !this.props.sankeyRows ||
                (this.props.sankeyRows && this.props.sankeyRows.length === 0)
              }
              onClick={handleChartCollapsing}
            >
              <MUIListItemIcon>
                {this.state.sankeyChartCollapsed ? (
                  <MUIVisibilityIcon fontSize="small" />
                ) : (
                  <MUIVisibilityOffIcon fontSize="small" />
                )}
              </MUIListItemIcon>
              {this.state.sankeyChartCollapsed ? "View Chart" : "Hide Chart"}
            </MUIMenuItem>
          </div>,
        ]}
      ></Menu>,
    ];
  }
}
